// What a Test records about its result, as the Test form edits it (TGCLOUD-1866, Phase 2 rework:
// "the Test record is the home of the result"). Pure: no React, no requests.
//
// A Test holds the genes it tested (`genesTested`, one row per gene: its scope, transcript,
// methods and the lab's coverage statement), the scope of the test as a whole (`testScope`), and
// its variants, each naming its gene. The Test save checks every row (backend
// services/testResults/testRecord.js) and refuses the whole save, with every reason, when one is
// wrong, so the form builds rows in exactly that shape and nothing else.
//
// THE VOCABULARIES ARE THE BACKEND'S (services/testResults/rules.js). testRecordModel.test.js reads
// that file and fails if these drift from it: a value the form offers and the save refuses is a
// Test that cannot be saved.
//
// A GENE IS ITS KEY. The key is the symbol upper-cased, with an HGNC search label's " — name"
// cut off, as the save derives it (testRecord.geneKeyOf): the variant editor stores a gene as the
// lookup's label, "BRCA1 — BRCA1 DNA repair associated", and the row and the variant must still
// be recognised as the same gene. The printed spelling is kept in `symbolAsPrinted`.
//
// The labels below are English UI text, translated where they are shown (`t(SCOPE_LABELS[s])`).

import { english } from 'src/i18n/language';

export const GENOME_BUILDS = Object.freeze(['GRCh37', 'GRCh38']);
export const GENE_TESTED_SCOPES = Object.freeze([
  'whole-gene',
  'region',
  'targeted-variant'
]);
export const TEST_SCOPE_KINDS = Object.freeze([
  'panel',
  'targeted',
  'single-site',
  'exome',
  'genome',
  'other'
]);
export const METHOD_KINDS = Object.freeze([
  'sequencing',
  'deletion-duplication',
  'repeat-sizing',
  'genotyping',
  'other'
]);
export const COVERAGE_STATUSES = Object.freeze([
  'complete',
  'incomplete',
  'not-stated'
]);

export const SCOPE_LABELS = Object.freeze({
  'whole-gene': 'Whole gene',
  region: 'Region',
  'targeted-variant': 'Targeted variant'
});
export const TEST_SCOPE_LABELS = Object.freeze({
  panel: 'Panel',
  targeted: 'Targeted',
  'single-site': 'Single site',
  exome: 'Exome',
  genome: 'Genome',
  other: 'Other'
});
export const COVERAGE_LABELS = Object.freeze({
  complete: 'Complete',
  incomplete: 'Incomplete',
  'not-stated': 'Not stated'
});
export const METHOD_KIND_LABELS = Object.freeze({
  sequencing: 'Sequencing',
  'deletion-duplication': 'Deletion/duplication',
  'repeat-sizing': 'Repeat sizing',
  genotyping: 'Genotyping',
  other: 'Other method'
});

// A versioned RefSeq or Ensembl accession, or an LRG (rules.TRANSCRIPT_PATTERN_SOURCE).
export const TRANSCRIPT_PATTERN_SOURCE =
  '^((NM|NR|XM|XR)_[0-9]+\\.[0-9]+|ENST[0-9]+\\.[0-9]+|LRG_[0-9]+(t[0-9]+)?)$';
const TRANSCRIPT_PATTERN = new RegExp(TRANSCRIPT_PATTERN_SOURCE);
export const isVersionedTranscript = (value) =>
  typeof value === 'string' && TRANSCRIPT_PATTERN.test(value);
export const TRANSCRIPT_HELP =
  'A versioned accession: NM_000059.4, ENST00000380152.8 or LRG_293.';

// rules.GENE_SYMBOL_KEY_PATTERN: what the save accepts as a gene.
const GENE_SYMBOL_KEY_PATTERN = /^[A-Z0-9][A-Z0-9-]*$/;
export const isGeneSymbolKey = (value) =>
  typeof value === 'string' && GENE_SYMBOL_KEY_PATTERN.test(value);

const hasText = (value) => typeof value === 'string' && /\S/.test(value);

/** The gene's key: "BRCA1 — BRCA1 DNA repair associated" and " brca1" are both "BRCA1". */
export const geneKeyOf = (value) =>
  typeof value === 'string' ? value.split(' — ')[0].trim().toUpperCase() : '';

/** A gene row's key. */
export const rowKeyOf = (row) =>
  geneKeyOf(row?.gene?.symbol || row?.gene?.symbolAsPrinted || '');

/** How a row names its gene to a person: as printed, else its key. */
export const rowSymbolOf = (row) =>
  (hasText(row?.gene?.symbolAsPrinted) && row.gene.symbolAsPrinted) ||
  rowKeyOf(row);

/**
 * A gene lookup id ('NCBIGene:672', 'HGNC:1100') as the row's ids. The terminology service keys a
 * gene by its NCBI Gene id, and by its HGNC id only where it has none.
 */
export function geneIdsOf(lookupId) {
  const id = typeof lookupId === 'string' ? lookupId : '';
  return {
    hgncId: /^HGNC:[0-9]+$/.test(id) ? id : null,
    ncbiGeneId: /^NCBIGene:[0-9]+$/.test(id) ? id : null
  };
}

/**
 * A gene row entered by hand: the whole gene, nothing stated about its coverage or methods. The
 * lab's coverage statement has no default (the save refuses a row without one), and `not-stated`
 * is the honest answer until someone enters what the lab said: it never reads as "nothing
 * reportable".
 */
export function newGeneRow({ symbol, symbolAsPrinted, hgncId, ncbiGeneId }) {
  const key = geneKeyOf(symbol);
  return {
    gene: {
      symbol: key,
      symbolAsPrinted: hasText(symbolAsPrinted) ? symbolAsPrinted : key,
      hgncId: hgncId || null,
      ncbiGeneId: ncbiGeneId || null
    },
    scope: 'whole-gene',
    transcript: null,
    methods: [],
    coverage: { status: 'not-stated' }
  };
}

/**
 * `rows` added to `genesTested`, never twice: a gene already listed keeps its row.
 *
 * @returns {{ next: object[], added: string[], alreadyListed: string[] }}
 */
export function addGeneRows(genesTested, rows) {
  const current = Array.isArray(genesTested) ? genesTested : [];
  const listed = new Set(current.map(rowKeyOf));
  const next = [...current];
  const added = [];
  const alreadyListed = [];
  for (const row of rows || []) {
    const key = rowKeyOf(row);
    if (!key) continue;
    if (listed.has(key)) {
      if (!alreadyListed.includes(key)) alreadyListed.push(key);
      continue;
    }
    listed.add(key);
    next.push(row);
    added.push(key);
  }
  return { next, added, alreadyListed };
}

// ── What a gene's row says ────────────────────────────────────────────────────────────────────

// Mirrors backend services/testResults/geneStatus.js for ONE Test, so the table says what "Genes
// tested" and the person's gene status say for the same rows.
export const GENE_STATE = Object.freeze({
  VARIANT: 'variant-reported',
  NOTHING: 'nothing-reportable',
  INCOMPLETE: 'coverage-incomplete'
});
export const GENE_STATE_FILTERS = Object.freeze([
  { value: GENE_STATE.VARIANT, label: 'Variant reported' },
  { value: GENE_STATE.NOTHING, label: 'Nothing reportable' },
  { value: GENE_STATE.INCOMPLETE, label: 'Coverage incomplete or not stated' }
]);

/** A variant counts once it describes something: a c., g. or p. (geneStatus.variantsOf). */
export const describesAVariant = (v) =>
  !!v && (hasText(v.variantC) || hasText(v.variantG) || hasText(v.variantP));

/**
 * The gene's state on this Test. "Nothing reportable" is deliberately narrow, because it is the
 * answer a clinician may act on by not testing again: the WHOLE gene, coverage the lab states was
 * complete, and no variant.
 */
export function geneStateOf(row, geneVariants = []) {
  if (geneVariants.some(describesAVariant)) return GENE_STATE.VARIANT;
  if (row?.scope === 'whole-gene' && row?.coverage?.status === 'complete') {
    return GENE_STATE.NOTHING;
  }
  return GENE_STATE.INCOMPLETE;
}

/**
 * Why a row with no variant is not "nothing reportable", in the words the table shows: never
 * claiming the lab reported a gap it did not (backend geneStatus.coverageOfTest).
 */
export function notCompleteReason(row) {
  if (row?.coverage?.status === 'incomplete') return 'Coverage incomplete';
  if (row?.scope && row.scope !== 'whole-gene') return 'Region only';
  return 'Coverage not stated';
}

/**
 * Each row with its key, its state and its variants (with their index in `variants`), in the
 * Test's order. One pass over each list, so a 500-gene panel costs nothing to recompute.
 */
export function buildGeneRows(genesTested, variants) {
  const byGene = new Map();
  (Array.isArray(variants) ? variants : []).forEach((variant, index) => {
    const key = geneKeyOf(variant?.gene);
    if (!key) return;
    if (!byGene.has(key)) byGene.set(key, []);
    byGene.get(key).push({ variant, index });
  });
  return (Array.isArray(genesTested) ? genesTested : []).map((row, index) => {
    const key = rowKeyOf(row);
    const entries = byGene.get(key) || [];
    return {
      key,
      index,
      row,
      symbol: rowSymbolOf(row),
      variants: entries,
      variantCount: entries.filter((e) => describesAVariant(e.variant)).length,
      state: geneStateOf(
        row,
        entries.map((e) => e.variant)
      )
    };
  });
}

/** The rows a search and a state filter leave, in order. `state` 'all' keeps every state. */
export function filterGeneRows(rows, { search = '', state = 'all' } = {}) {
  const term = String(search || '')
    .trim()
    .toUpperCase();
  return rows.filter(
    (r) =>
      (state === 'all' || r.state === state) &&
      (!term ||
        r.key.includes(term) ||
        String(r.symbol || '')
          .toUpperCase()
          .includes(term))
  );
}

/** How many rows are in each state: { all, 'variant-reported': n, ... }. */
export function countGeneStates(rows) {
  const counts = { all: rows.length };
  for (const f of GENE_STATE_FILTERS) counts[f.value] = 0;
  for (const r of rows) counts[r.state] = (counts[r.state] || 0) + 1;
  return counts;
}

// ── What the gene list is (TGCLOUD-1868 B2) ───────────────────────────────────────────────────

// rules.GENES_TESTED_LIST_STATUSES: what a Test's gene rows are, saved as
// `genesTestedList: { status }`. Required on a save that applies a lab report, asked with no default
// when the genes are entered by hand.
export const GENES_TESTED_LIST_STATUSES = Object.freeze([
  'complete',
  'partial',
  'none'
]);
export const GENES_TESTED_LIST_LABELS = Object.freeze({
  complete: 'These are every gene the laboratory analysed',
  partial: 'Only some of the genes are recorded',
  none: 'No gene list was captured from this report'
});

// rules.SCOPES_WITHOUT_COMPLETE_GENE_LIST (TGCLOUD-1868, decision 13): an exome or a genome is
// recorded by its scope, not gene by gene, so its gene list is never every gene the laboratory
// analysed. The save refuses `complete` for one (GENES_TESTED_LIST_COMPLETE_SCOPE), so the form
// never offers it.
export const SCOPES_WITHOUT_COMPLETE_GENE_LIST = Object.freeze([
  'exome',
  'genome'
]);
const GENES_TESTED_LIST_STATUSES_OF_AN_EXOME = Object.freeze(
  GENES_TESTED_LIST_STATUSES.filter((status) => status !== 'complete')
);

/** The gene list states to offer for a Test of this scope (its `testScope.kind`, or none). */
export const genesTestedListStatusesFor = (testScopeKind) =>
  SCOPES_WITHOUT_COMPLETE_GENE_LIST.includes(testScopeKind)
    ? GENES_TESTED_LIST_STATUSES_OF_AN_EXOME
    : GENES_TESTED_LIST_STATUSES;

// A gene the Test does not list, as backend geneStatus.unlistedStatusOfTest says it: "not tested"
// only from a complete list that names a gene. Any other Test, one saved before the list state
// existed included, has not recorded whether the gene was tested.
export const UNLISTED_GENE_STATE = Object.freeze({
  NOT_TESTED: 'not-tested',
  NOT_RECORDED: 'not-recorded'
});
export const UNLISTED_GENE_STATE_LABELS = Object.freeze({
  [UNLISTED_GENE_STATE.NOT_TESTED]: 'Not tested',
  [UNLISTED_GENE_STATE.NOT_RECORDED]: 'Not recorded'
});
// What a gene this Test does not list reads as, said under its gene list and when a search finds no
// gene (TGCLOUD-1868 review: plan B2's mirror, GenesTestedTable). "Not recorded" is said in words,
// so that nobody reads a gene's absence as "not tested" where the Test never said so (hazard 1868-H4).
export const UNLISTED_GENE_MEANING = Object.freeze({
  [UNLISTED_GENE_STATE.NOT_TESTED]:
    'A gene not listed here reads as not tested: this is every gene the laboratory analysed.',
  [UNLISTED_GENE_STATE.NOT_RECORDED]:
    'A gene not listed here reads as not recorded: this Test does not say whether it was tested.'
});

/**
 * What this Test says about a gene it does not list: not tested, or not recorded. "Not tested" only
 * from a complete list that names a gene, holds every gene its report states it analysed (the
 * server's `statedCount`), and is not of an exome or a genome: the backend's hasCompleteList, which
 * testRecordModel.test.js binds this to.
 */
export function unlistedGeneStateOf(test) {
  const list = test?.genesTestedList;
  const rows = Array.isArray(test?.genesTested) ? test.genesTested.length : 0;
  const stated =
    Number.isInteger(list?.statedCount) && list.statedCount >= 1
      ? list.statedCount
      : null;
  const complete =
    list?.status === 'complete' &&
    rows > 0 &&
    (stated === null || rows >= stated) &&
    genesTestedListStatusesFor(test?.testScope?.kind).includes('complete');
  return complete
    ? UNLISTED_GENE_STATE.NOT_TESTED
    : UNLISTED_GENE_STATE.NOT_RECORDED;
}

// ── A pasted list of genes ────────────────────────────────────────────────────────────────────

/**
 * A pasted list, as the symbols it names: split on commas, semicolons, spaces and new lines, each
 * keyed, a repeat dropped. Nothing is dropped silently: a token that is not a gene symbol is
 * returned with `valid: false`, for the dialog to show.
 *
 * @returns {Array<{ asTyped: string, key: string, valid: boolean }>}
 */
export function parseSymbolList(text) {
  const seen = new Set();
  const out = [];
  for (const token of String(text || '').split(/[\s,;]+/)) {
    const asTyped = token.trim();
    if (!asTyped) continue;
    const key = geneKeyOf(asTyped);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ asTyped, key, valid: isGeneSymbolKey(key) });
  }
  return out;
}

// ── Variants ──────────────────────────────────────────────────────────────────────────────────

const HGVS_KEYS = [
  ['variantC', 'c.'],
  ['variantG', 'g.'],
  ['variantP', 'p.']
];

/**
 * An HGVS description as a key: the reference sequence before the last ':' cut off
 * ("NM_000249.4:c.1852_1854del" is "c.1852_1854del"), spaces removed, and the prefix put back
 * where it was typed without one. Case is kept: it means something in HGVS.
 */
export function hgvsKeyOf(value, prefix) {
  if (!hasText(value)) return '';
  const trimmed = value.trim();
  const colon = trimmed.lastIndexOf(':');
  let tail = (colon >= 0 ? trimmed.slice(colon + 1) : trimmed).replace(
    /\s+/g,
    ''
  );
  if (!/^[a-z]\./.test(tail)) tail = `${prefix}${tail}`;
  return tail;
}

/**
 * Are two variants the same variant? The same finding of a report; otherwise the same gene and the
 * same description at the most specific level both give (c., then g., then p.), so a variant
 * entered with its c. alone is found again when the report also gives its p.
 */
export function sameVariant(a, b) {
  if (!a || !b) return false;
  if (a.findingId && b.findingId && a.findingId === b.findingId) return true;
  const gene = geneKeyOf(a.gene);
  if (!gene || gene !== geneKeyOf(b.gene)) return false;
  for (const [key, prefix] of HGVS_KEYS) {
    const x = hgvsKeyOf(a[key], prefix);
    const y = hgvsKeyOf(b[key], prefix);
    if (x && y) return x === y;
  }
  return false;
}

/**
 * The Test's variants grouped by gene, in the order each gene first appears, each with its index
 * in `variants`. A variant with no gene is grouped under ''.
 */
export function groupVariantsByGene(variants) {
  const groups = new Map();
  (Array.isArray(variants) ? variants : []).forEach((variant, index) => {
    const key = geneKeyOf(variant?.gene);
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        label:
          (typeof variant?.gene === 'string' &&
            variant.gene.split(' — ')[0].trim()) ||
          key,
        entries: []
      });
    }
    groups.get(key).entries.push({ variant, index });
  });
  return [...groups.values()];
}

// ── What a variant from a lab report adds ─────────────────────────────────────────────────────

export const STATED_VALUE_LABELS = Object.freeze({
  heterozygous: 'Heterozygous',
  homozygous: 'Homozygous',
  hemizygous: 'Hemizygous',
  heteroplasmic: 'Heteroplasmic',
  homoplasmic: 'Homoplasmic',
  mosaic: 'Mosaic',
  'de-novo': 'De novo',
  maternal: 'Maternal',
  paternal: 'Paternal',
  'not-determined': 'Not determined'
});

// rules.ZYGOSITIES: what a variant's zygosity may be, chosen from a list with NO default where the
// reading was not confident of it (TGCLOUD-1868 D3). "Not stated" is a value: choosing it says the
// report does not state one.
export const ZYGOSITIES = Object.freeze([
  'heterozygous',
  'homozygous',
  'hemizygous',
  'heteroplasmic',
  'homoplasmic',
  'mosaic',
  'not-stated'
]);
export const ZYGOSITY_LABELS = Object.freeze({
  heterozygous: STATED_VALUE_LABELS.heterozygous,
  homozygous: STATED_VALUE_LABELS.homozygous,
  hemizygous: STATED_VALUE_LABELS.hemizygous,
  heteroplasmic: STATED_VALUE_LABELS.heteroplasmic,
  homoplasmic: STATED_VALUE_LABELS.homoplasmic,
  mosaic: STATED_VALUE_LABELS.mosaic,
  'not-stated': 'Not stated'
});

/**
 * A `{ value, asPrinted }` as a person reads it: the lab's words, else the value's label, else
 * null. `t` translates the label only: the lab's own words are shown as printed.
 */
export function statedValueText(stated, t = english) {
  if (!stated || !stated.value || stated.value === 'not-stated') return null;
  return (
    (hasText(stated.asPrinted) && stated.asPrinted) ||
    (STATED_VALUE_LABELS[stated.value] &&
      t(STATED_VALUE_LABELS[stated.value])) ||
    stated.value
  );
}

/** Did this variant come from a lab report? (It then needs its build and transcript to save.) */
export const isReportVariant = (v) => !!(v && v.labReportId);
